import {
  FormikErrorMessage,
  Modal,
  Row,
  Section,
  Button,
  TextInput
} from 'shared'
import { Formik, Form, FormikHelpers, FormikProps } from 'formik'
import { Panel, Col, ButtonToolbar, SelectPicker, Toggle } from 'rsuite'
import { notifyError, notifySuccess } from 'utils'
import useAuth from '../../../hooks/Auth'
import './CreateIntegration.scss'

import {
  defaultIntegrationFormValues,
  INTEGRATION_FORM_FIELDS,
  IntegrationForm,
  getIntegrationValidationSchema
} from '../utils'
import { useCreateIntegrationMutation } from '../integrationApiSlice'
import { Integration, INTEGRATION_SYSTEM_TYPES } from '../types'

interface CreateAddIntegrationProps {
  open: boolean;
  onClose: () => void;
  isEditMode?: boolean;
  existingIntegrationSystems?: string[];
}

const AddIntegration: React.FC<CreateAddIntegrationProps> = ({
  open,
  onClose,
  isEditMode,
  existingIntegrationSystems
}) => {
  const { selectedClientId } = useAuth()
  const clientId = selectedClientId ? Number(selectedClientId) : undefined
  const [createIntegration] = useCreateIntegrationMutation()
  const { INTEGRATION_SYSTEM, ACTIVE } = INTEGRATION_FORM_FIELDS

  const initialValues = defaultIntegrationFormValues

  const onSubmit = async (
    formValues: IntegrationForm,
    { setSubmitting }: FormikHelpers<IntegrationForm>
  ) => {
    if (!clientId) {
      notifyError('Client ID is missing.', { containerId: 'modalToast' })
      setSubmitting(false)
      return
    }
    if (existingIntegrationSystems?.includes(formValues.integrationSystem)) {
      notifyError('Integration type already exists.', {
        containerId: 'modalToast'
      })
      setSubmitting(false)
      return
    }
    const integrationDTO: Integration = {
      name: formValues.name,
      storageBucketName: formValues.storageBucketName,
      active: formValues.active,
      sourceSystem: {
        id: clientId
      },
      targetSystem: {
        id: clientId
      },
      integrationSystem: formValues.integrationSystem
    }
    try {
      await createIntegration({ integrationDTO }).unwrap()
      notifySuccess('Integration created successfully!')

      onClose()
    } catch (error) {
      notifyError('Failed to save Integration', {
        containerId: 'modalToast'
      })
    } finally {
      setSubmitting(false)
    }
  }
  const renderFormButtons = (formikProps: FormikProps<IntegrationForm>) => (
    <ButtonToolbar>
      {isEditMode ? (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save changes
          </Button>
        </>
      ) : (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save
          </Button>
        </>
      )}
    </ButtonToolbar>
  )

  return (
    <Modal
      secondary
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Client Integration' : 'Add Client Integration'}
      size="calc(50% - 120px)"
      body={
        <Formik
          initialValues={initialValues}
          validationSchema={getIntegrationValidationSchema}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {(formikProps: FormikProps<IntegrationForm>) => (
            <Form className="create-edit">
              <Panel>
                <Section title="Integration Type Deatails">
                  <Row>
                    <Col xs={12}>
                      <SelectPicker
                        name={INTEGRATION_FORM_FIELDS.INTEGRATION_SYSTEM}
                        data={Object.values(INTEGRATION_SYSTEM_TYPES).map(
                          (type) => ({
                            label: type,
                            value: type
                          })
                        )}
                        value={formikProps.values.integrationSystem}
                        placeholder="Select Integration Type"
                        onChange={(value) => {
                          if (
                            value &&
                            existingIntegrationSystems?.includes(value)
                          ) {
                            notifyError(
                              'This integration type already exists.',
                              {
                                containerId: 'modalToast'
                              }
                            )
                          }
                          formikProps.setFieldValue(
                            INTEGRATION_FORM_FIELDS.INTEGRATION_SYSTEM,
                            value || ''
                          )
                        }}
                        disabled={isEditMode}
                        block
                        searchable={false}
                      />

                      <FormikErrorMessage name={INTEGRATION_SYSTEM} />
                    </Col>
                  </Row>
                </Section>
                <Section title="Integration Details">
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={INTEGRATION_FORM_FIELDS.NAME}
                        placeholder="Enter Integration Name"
                        dataType="string"
                      />
                      <FormikErrorMessage name={INTEGRATION_FORM_FIELDS.NAME} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={INTEGRATION_FORM_FIELDS.STORAGE_BUCKET_NAME}
                        placeholder="Enter Storage Bucket Name"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={INTEGRATION_FORM_FIELDS.STORAGE_BUCKET_NAME}
                      />
                    </Col>
                  </Row>
                </Section>
                <Section title="Status">
                  <Row>
                    <Col xs={24} md={12}>
                      <Toggle
                        checked={
                          formikProps.values[INTEGRATION_FORM_FIELDS.ACTIVE]
                        }
                        onChange={() =>
                          formikProps.setFieldValue(
                            INTEGRATION_FORM_FIELDS.ACTIVE,
                            !formikProps.values[INTEGRATION_FORM_FIELDS.ACTIVE]
                          )
                        }
                        data-testid="toggle-active"
                      />
                      <span className="toogle-text">Set as Active</span>
                      <FormikErrorMessage name={ACTIVE} />
                    </Col>
                  </Row>
                </Section>
              </Panel>
              <Panel className="panel-buttons">
                {renderFormButtons(formikProps)}
              </Panel>
            </Form>
          )}
        </Formik>
      }
    />
  )
}

export default AddIntegration
